// Every printable on the site. `generic: true` entries are rendered by
// PrintablePuzzlePage from the game's own level file; the others have their own
// page components. The /printable hub and the weekly pack read this list too.

const levels = (dir) => `/${dir}/assets/assets/levels/all_levels.json`;
// Printables whose web game was replaced by a Century app build with a different
// level format keep their original puzzle set here (scripts/build-century-web.sh).
const printableData = (dir) => `/printable-data/${dir}/all_levels.json`;

const bySize = (p) => String(p.size);
const sizeLabel = (v) => `${v}x${v}`;

export const PRINTABLES = [
  {
    slug: 'crowns',
    name: 'Crowns',
    blurb: 'Place one crown in every row, column and colour region.',
  },
  {
    slug: 'nonograms',
    name: 'Nonograms',
    blurb: 'Picture logic puzzles: shade cells to match the row and column clues.',
  },
  {
    slug: 'sudoku',
    name: 'Sudoku',
    blurb: 'Classic 9x9 Sudoku in easy, medium and hard.',
  },
  {
    slug: 'calcudoku',
    name: 'Calcudoku',
    blurb: 'Arithmetic cages on a Latin square.',
  },
  {
    slug: 'tent-logic',
    name: 'Tent Logic',
    blurb: 'Pitch one tent beside every tree, with no two tents touching.',
  },
  {
    slug: 'binary-sudoku-plus',
    name: 'Binary Sudoku',
    blurb: 'Fill the grid with 0s and 1s, never three in a row.',
  },
  {
    slug: 'aquarium',
    name: 'Aquarium',
    blurb: 'Fill the tanks with water to match the edge counts.',
  },
  {
    slug: 'logic-grid-puzzles',
    name: 'Logic Grid Puzzles',
    generic: true,
    gameSlug: 'logic-grid-puzzles',
    playPath: '/play/logicgridpuzzles',
    dataUrl: printableData('logicgridpuzzles'),
    renderer: 'logicgrid',
    plural: 'Logic Grid Puzzles',
    blurb: 'Zebra-puzzle riddles with a full elimination grid and clue list.',
    sizeOf: (p) => `${p.categories.length}x${p.categories[0].items.length}`,
    sizeLabel: (v) => ({ '3x3': 'Easy (3x3)', '4x4': 'Medium (4x4)', '5x5': 'Hard (5x5)' }[v] || v),
    counts: [1, 2],
    defaultCount: 2,
    defaultSize: '4x4',
    title: 'Free Printable Logic Grid Puzzles PDF with Answers | Logic Puzzles CA',
    description: 'Free printable logic grid puzzles with answer keys. Zebra-puzzle deduction riddles in easy, medium and hard, each with a full elimination grid and clue list. Great for classrooms and puzzle lovers.',
    keywords: 'printable logic grid puzzles, logic grid puzzles pdf, logic puzzles with grid printable, zebra puzzle printable, logic grid worksheets',
    h1: 'Free Printable Logic Grid Puzzles',
    lead: 'Zebra-puzzle deduction riddles with the elimination grid already drawn. Print a sheet, grab a pencil and start crossing off.',
    about: 'A <strong>logic grid puzzle</strong> gives you a few categories (names, pets, colours, jobs) and a list of clues. Every item matches exactly one item in each other category. The grid on the sheet lets you record what you learn: an X for impossible pairs, a check for confirmed matches. When every block has one check per row and column, you have the answer.',
    rules: [
      ['Read every clue', 'Each clue either links two items ("Alice owns the dog") or rules a pair out ("The pilot does not like red").'],
      ['Mark the grid', 'Put an X where a pairing is impossible and a check where it is confirmed. A check rules out the rest of its row and column in that block.'],
      ['Carry matches across', 'If Alice owns the dog and the dog owner likes red, then Alice likes red. Copy what you know between blocks.'],
      ['Last one standing', 'When only one cell in a row of a block is left without an X, it must be the match.'],
    ],
    legend: [
      'Each puzzle uses the staircase grid: every category meets every other category in exactly one block.',
      'The answer key lists the full match for every item in the first category.',
    ],
    faq: [
      ['Are these logic grid puzzles good for kids?', 'The easy 3x3 puzzles are a gentle first step for kids and beginners. The 5x5 puzzles will challenge adults.'],
      ['Does every puzzle have one answer?', 'Yes. Every puzzle has a single solution you can reach from the clues alone, without guessing.'],
    ],
  },
  {
    slug: 'norinori',
    name: 'NoriNori',
    generic: true,
    gameSlug: 'norinori',
    playPath: '/play/norinori',
    dataUrl: levels('norinori'),
    renderer: 'norinori',
    blurb: 'Shade two cells in every region so the shaded cells form dominoes.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable NoriNori Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Download free printable NoriNori logic puzzles with solutions. Shade two cells in every region to form dominoes. 5x5, 7x7 and 10x10 grids, ready to print for classrooms or offline solving.',
    keywords: 'printable norinori, norinori pdf, norinori puzzles printable, nori nori puzzle, printable logic puzzles',
    h1: 'Free Printable NoriNori Puzzles',
    lead: 'Shade two cells in every outlined region so that all the shaded cells pair up into dominoes. Print a fresh sheet any time.',
    about: '<strong>NoriNori</strong> is a Japanese shading puzzle. The grid is split into outlined regions. You shade exactly two cells in each region, and every shaded cell must touch exactly one other shaded cell, so the shading forms a set of dominoes that never touch side to side.',
    rules: [
      ['Two per region', 'Every outlined region contains exactly two shaded cells.'],
      ['Dominoes only', 'Each shaded cell touches exactly one other shaded cell, side by side.'],
      ['No touching', 'Different dominoes may touch at corners but never along an edge.'],
      ['Across borders', 'A domino may cross a region border, counting once for each region.'],
    ],
    legend: ['Thick lines outline the regions.', 'Shaded cells in the answer key show the dominoes.'],
    faq: [
      ['Where should I start a NoriNori?', 'Look for two-cell regions: both cells are shaded, so every cell around them stays white.'],
    ],
  },
  {
    slug: 'slant',
    name: 'Slant',
    generic: true,
    gameSlug: 'slant',
    playPath: '/play/slant',
    dataUrl: levels('slant'),
    renderer: 'slant',
    blurb: 'Draw a diagonal in every cell to match the corner numbers, with no loops.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Slant Puzzles PDF (Gokigen Naname) | Logic Puzzles CA',
    description: 'Free printable Slant puzzles, also called Gokigen Naname, with solutions. Draw a diagonal in every cell so the circled numbers match and no loops form. 5x5, 7x7 and 10x10 PDF worksheets.',
    keywords: 'printable slant puzzle, gokigen naname printable, slant puzzle pdf, printable logic puzzles, diagonal puzzle',
    h1: 'Free Printable Slant Puzzles',
    lead: 'Draw a / or \\ in every cell so each circled number touches that many lines, and never close a loop.',
    about: '<strong>Slant</strong> (Gokigen Naname in Japanese) is a pencil puzzle about diagonal lines. Every cell gets one diagonal. The numbers sit on the grid corners and say how many diagonals meet at that point. The finished lines must never form a closed loop.',
    rules: [
      ['One line per cell', 'Every cell holds a single diagonal, either / or \\.'],
      ['Corner numbers', 'A number on a corner says how many diagonals touch that corner (0 to 4).'],
      ['No loops', 'The diagonals can never join up into a closed loop.'],
      ['Unnumbered corners', 'Corners without a number can touch any number of lines.'],
    ],
    legend: ['Circled numbers sit on grid corners.', 'The answer key shows every diagonal.'],
    faq: [
      ['What do 0 and 4 mean?', 'A 0 means no line touches that corner, which fixes all four cells around it. A 4 means all of them point at it.'],
    ],
  },
  {
    slug: 'kurodoku',
    name: 'Kurodoku',
    generic: true,
    gameSlug: 'kurodoku',
    playPath: '/play/kurodoku',
    dataUrl: levels('kurodoku'),
    renderer: 'kurodoku',
    blurb: 'Shade cells so each number counts the white cells it can see.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Kurodoku Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Download free printable Kurodoku (Kuromasu) puzzles with answers. Shade cells so every number counts the white cells it can see. 6x6, 8x8 and 10x10 PDF worksheets.',
    keywords: 'printable kurodoku, kuromasu printable, kurodoku pdf, printable logic puzzles, shading puzzle printable',
    h1: 'Free Printable Kurodoku Puzzles',
    lead: 'Shade cells black so that every number sees exactly that many white cells. Print a sheet and solve it with a pencil.',
    about: '<strong>Kurodoku</strong> (also called Kuromasu) is a Japanese shading puzzle. Each number tells you how many white cells it can see along its row and column, counting itself, until the view is blocked by a black cell or the edge of the grid.',
    rules: [
      ['Visibility numbers', 'A number counts the white cells visible up, down, left and right, including its own cell.'],
      ['Numbers stay white', 'A cell with a number is never shaded.'],
      ['No touching black cells', 'Two black cells may not share an edge.'],
      ['One white area', 'All white cells must connect into a single group.'],
    ],
    legend: ['Shaded cells in the answer key are the black cells.'],
    faq: [
      ['Is Kurodoku the same as Kuromasu?', 'Yes. Kurodoku and Kuromasu are two names for the same puzzle.'],
    ],
  },
  {
    slug: 'renzoku',
    name: 'Renzoku',
    generic: true,
    gameSlug: 'renzoku',
    playPath: '/play/renzoku',
    dataUrl: levels('renzoku'),
    renderer: 'renzoku',
    blurb: 'A Latin square where dots mark consecutive neighbours.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Renzoku Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Renzoku puzzles with answers. Fill each row and column with 1 to N; a dot between two cells means consecutive numbers. 4x4, 5x5 and 7x7 PDF worksheets.',
    keywords: 'printable renzoku, renzoku pdf, renzoku puzzle printable, consecutive puzzle printable, printable number puzzles',
    h1: 'Free Printable Renzoku Puzzles',
    lead: 'Fill every row and column with 1 to N. A dot means the two numbers are consecutive, and no dot means they are not.',
    about: '<strong>Renzoku</strong> is a number puzzle built on a Latin square, like Sudoku without boxes. The dots between cells do the work: a dot means the two numbers differ by exactly one, and a missing dot means they do not.',
    rules: [
      ['Latin square', 'Each row and column holds every number from 1 to N exactly once.'],
      ['Dot = consecutive', 'Two cells joined by a dot hold consecutive numbers, like 3 and 4.'],
      ['No dot = not consecutive', 'Neighbours without a dot never differ by one.'],
      ['Givens', 'A few numbers are printed to get you started.'],
    ],
    legend: ['Dots sit on the line between two cells.', 'The answer key prints the given numbers in bold.'],
    faq: [
      ['Where do I start a Renzoku?', 'Look at the smallest and largest numbers: 1 and N have only one consecutive partner each, so a cell with a dot on each of two opposite sides can never hold them.'],
    ],
  },
  {
    slug: 'dominosa',
    name: 'Dominosa',
    generic: true,
    gameSlug: 'dominosa',
    playPath: '/play/dominosa',
    dataUrl: levels('dominosa'),
    renderer: 'dominosa',
    blurb: 'Split the number grid into a complete set of dominoes.',
    sizeOf: (p) => String(p.maxNumber),
    sizeLabel: (v) => `Double-${v} set`,
    title: 'Free Printable Dominosa Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Download free printable Dominosa puzzles with answers. Divide the grid of numbers into a full set of dominoes, each pair used exactly once. Three sizes of PDF worksheets.',
    keywords: 'printable dominosa, dominosa pdf, domino puzzle printable, dominosa puzzles, printable logic puzzles',
    h1: 'Free Printable Dominosa Puzzles',
    lead: 'Draw the dominoes back onto a grid of numbers. Every pair appears exactly once, so each domino you find rules others out.',
    about: '<strong>Dominosa</strong> starts with a full set of dominoes laid out in a rectangle, with the outlines erased. Your job is to draw them back in. Because every pair from the set appears exactly once, finding one domino tells you a lot about where the others cannot be.',
    rules: [
      ['Pair the cells', 'Split the whole grid into dominoes, each covering two side-by-side cells.'],
      ['Every pair once', 'A double-6 set has every pair from 0-0 to 6-6 exactly once.'],
      ['No repeats', '2-5 and 5-2 are the same domino, so it appears only once.'],
      ['Full coverage', 'Every number belongs to exactly one domino.'],
    ],
    legend: ['A double-4 set uses the numbers 0 to 4, a double-6 set 0 to 6, and so on.', 'The answer key outlines every domino.'],
    faq: [
      ['How do I start a Dominosa?', 'Find a pair that can only sit in one place in the whole grid. That domino is forced, and its cells are now used up.'],
    ],
  },
  {
    slug: 'light-up',
    name: 'Light Up',
    generic: true,
    gameSlug: 'light-up',
    playPath: '/play/lightup',
    dataUrl: levels('lightup'),
    renderer: 'lightup',
    blurb: 'Place bulbs to light every cell without two bulbs seeing each other.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Light Up Puzzles PDF | Logic Puzzles CA',
    description: 'Free printable Light Up puzzles with solutions. Place bulbs so every white cell is lit and no two bulbs shine on each other. 5x5, 7x7 and 10x10 PDF worksheets.',
    keywords: 'printable light up puzzle, light bulb puzzle printable, light up puzzle pdf, printable logic puzzles',
    h1: 'Free Printable Light Up Puzzles',
    lead: 'Place light bulbs so every white cell is lit, no two bulbs shine on each other, and the numbered walls get exactly that many bulbs.',
    about: '<strong>Light Up</strong> is a Japanese puzzle about light bulbs in a room of black walls. A bulb lights its whole row and column until a wall stops it. Fill the room with light using bulbs that never see each other.',
    rules: [
      ['Light every cell', 'Every white cell must be lit by at least one bulb.'],
      ['Bulbs never see each other', 'Two bulbs may not share a row or column unless a wall sits between them.'],
      ['Numbered walls', 'A number on a wall says how many bulbs touch it on its four sides.'],
      ['Plain walls', 'Walls without a number can have any number of bulbs next to them.'],
    ],
    legend: ['Black cells are walls.', 'Circles in the answer key are bulbs.'],
    faq: [
      ['Where does Light Up come from?', 'It was created in Japan by a puzzle magazine; Light Up is its common English name.'],
    ],
  },
  {
    slug: 'mosaic',
    name: 'Mosaic',
    generic: true,
    gameSlug: 'mosaic',
    playPath: '/play/mosaic',
    dataUrl: printableData('mosaic'),
    renderer: 'mosaic',
    blurb: 'Fill cells so each number counts the filled cells around it, revealing a picture.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Mosaic Puzzles PDF | Logic Puzzles CA',
    description: 'Free printable Mosaic picture logic puzzles with answers. Each number counts the filled cells in its 3x3 block. 5x5, 7x7 and 10x10 PDF worksheets.',
    keywords: 'printable mosaic puzzle, picture logic printable, mosaic puzzle pdf, number clue picture puzzle, printable logic puzzles',
    h1: 'Free Printable Mosaic Puzzles',
    lead: 'Each number tells you how many cells in its 3x3 block are filled. Shade them all in and a picture appears.',
    about: '<strong>Mosaic</strong> mixes mine-style neighbor counting with picture puzzles. Every number counts the filled cells in the 3x3 block around it, including its own cell. Work through the overlapping blocks and the hidden pattern fills itself in.',
    rules: [
      ['Count the block', 'A number counts the filled cells in its 3x3 block, itself included.'],
      ['Fill or leave blank', 'Every cell ends up either filled or empty.'],
      ['Edges count less', 'Blocks on the edge have 6 cells and corner blocks have 4.'],
      ['Use the overlaps', 'Neighbouring numbers share cells, and comparing them is where most deductions come from.'],
    ],
    legend: ['Shaded cells in the answer key are the filled cells.'],
    faq: [
      ['Where do I start a Mosaic?', 'Look for 0s and 9s: a 0 empties its whole block and a 9 fills it.'],
    ],
  },
  {
    slug: 'galaxies',
    name: 'Galaxies',
    generic: true,
    gameSlug: 'galaxies',
    playPath: '/play/galaxies',
    dataUrl: levels('galaxies'),
    renderer: 'galaxies',
    blurb: 'Split the grid into regions that are symmetric around their dots.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Galaxies Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Galaxies puzzles with answers. Divide the grid into regions, each with 180-degree rotational symmetry around its dot. 5x5, 7x7 and 9x9 PDF worksheets.',
    keywords: 'printable galaxies puzzle, galaxies puzzle pdf, symmetry puzzle printable, galaxy logic puzzle, printable logic puzzles',
    h1: 'Free Printable Galaxies Puzzles',
    lead: 'Draw lines along the grid to split it into galaxies. Each galaxy has one dot and looks the same when you turn the page upside down around that dot.',
    about: '<strong>Galaxies</strong> is a symmetry puzzle. Every dot is the centre of one region, called a galaxy. Dots can sit in a cell, on an edge or on a corner. Each galaxy must have 180-degree rotational symmetry around its dot, and together the galaxies cover the whole grid.',
    rules: [
      ['One dot per galaxy', 'Every region contains exactly one dot, which is its centre.'],
      ['Turn it around', 'Spin a galaxy half a turn around its dot and it covers the same cells.'],
      ['Cover the grid', 'Every cell belongs to exactly one galaxy.'],
      ['Mirror pairs', 'If a cell belongs to a galaxy, the cell opposite it through the dot does too.'],
    ],
    legend: ['Circles are the galaxy centres.', 'Thick lines in the answer key are the galaxy borders.'],
    faq: [
      ['Where do I start a Galaxies puzzle?', 'Start with cells right next to a dot: a dot in the middle of an edge always owns both cells it touches, and a dot on a corner owns all four.'],
    ],
  },
  {
    slug: 'thermometers',
    name: 'Thermometers',
    generic: true,
    gameSlug: 'thermometers',
    playPath: '/play/thermometers',
    dataUrl: levels('thermometers'),
    renderer: 'thermometers',
    plural: 'Thermometer Puzzles',
    blurb: 'Fill thermometers from the bulb up to match the row and column counts.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Thermometer Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Thermometer puzzles with answers. Fill each thermometer from the bulb up so every row and column has the right number of filled cells. 5x5, 7x7 and 10x10 PDF worksheets.',
    keywords: 'printable thermometer puzzle, thermometer puzzle pdf, thermometers puzzle printable, thermometer logic puzzle, printable logic puzzles',
    h1: 'Free Printable Thermometer Puzzles',
    lead: 'Shade the mercury in each thermometer, always starting at the bulb, so the numbers outside the grid match.',
    about: 'A <strong>thermometer puzzle</strong> fills a grid with thermometers of different lengths. The numbers above the columns and beside the rows say how many cells are filled in each. Mercury always rises from the bulb with no gaps, so a thermometer can be empty, partly filled or full.',
    rules: [
      ['Row and column counts', 'The number beside a row, or above a column, is how many of its cells are filled.'],
      ['Start at the bulb', 'Mercury begins at the round bulb and rises toward the flat end.'],
      ['No gaps', 'A cell can only be filled if every cell between it and the bulb is filled too.'],
      ['Any level', 'A thermometer can be empty, partly filled or completely full.'],
    ],
    legend: ['Numbers on top count filled cells in each column; numbers on the left count each row.', 'Shaded cells in the answer key are the mercury.'],
    faq: [
      ['Where do I start a thermometer puzzle?', 'Look for rows or columns with a 0 (nothing filled) or a count equal to the grid size (everything filled), then follow each thermometer back to its bulb.'],
    ],
  },
  {
    slug: 'yin-yang',
    name: 'Yin-Yang',
    generic: true,
    gameSlug: 'yin-yang',
    playPath: '/play/yinyang',
    dataUrl: levels('yinyang'),
    renderer: 'yinyang',
    plural: 'Yin Yang Puzzles',
    blurb: 'Fill the grid with black and white stones, each colour all connected.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Yin Yang Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Yin Yang logic puzzles with answers. Fill every cell with a black or white stone so each colour stays connected and no 2x2 block is one colour. 6x6, 8x8 and 10x10 PDF worksheets.',
    keywords: 'printable yin yang puzzle, yin yang puzzle pdf, yin yang logic puzzle, black and white puzzle printable, printable logic puzzles',
    h1: 'Free Printable Yin Yang Puzzles',
    lead: 'Put a black or white stone in every cell. All the black stones must join up, all the white stones must join up, and no 2x2 block can be a single colour.',
    about: '<strong>Yin-Yang</strong> is a two-colour connection puzzle. A few stones are printed to start. You fill every other cell with black or white so that each colour forms one connected group, moving up, down, left and right.',
    rules: [
      ['Every cell gets a stone', 'Each cell is either black or white.'],
      ['Black connects', 'All black stones form one group joined along edges.'],
      ['White connects', 'All white stones form one group joined along edges.'],
      ['No 2x2 blocks', 'No 2x2 square may be all black or all white.'],
    ],
    legend: ['Printed stones are the givens.', 'The answer key shows every stone.'],
    faq: [
      ['What is a good first move?', 'Walk around the border. The colour can change at most twice on the way round: if the edge went black, white, black, white, one colour could never join up. Use that to fill the edge cells first.'],
    ],
  },
  {
    slug: 'skyscrapers',
    name: 'Skyscrapers',
    generic: true,
    gameSlug: 'skyscrapers',
    playPath: '/play/skyscrapers',
    dataUrl: levels('skyscrapers'),
    renderer: 'skyscrapers',
    blurb: 'Place buildings of every height so the edge numbers count what you can see.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Skyscrapers Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Skyscrapers puzzles with answers. Fill the grid with building heights 1 to N; the edge numbers say how many buildings you can see from that side. 4x4, 5x5 and 6x6 PDF worksheets.',
    keywords: 'printable skyscrapers puzzle, skyscrapers puzzle pdf, skyscraper logic puzzle printable, building puzzle printable, printable number puzzles',
    h1: 'Free Printable Skyscrapers Puzzles',
    lead: 'Each row and column holds one building of every height. The numbers around the edge tell you how many buildings you can see from there.',
    about: '<strong>Skyscrapers</strong> is a number puzzle on a Latin square. Every row and column contains each height from 1 to N once. Taller buildings hide shorter ones behind them, so a clue of 1 means the tallest building is right at the edge, and a clue of N means the buildings rise one step at a time.',
    rules: [
      ['Latin square', 'Every row and column holds each height from 1 to N exactly once.'],
      ['Count what you see', 'An edge number is how many buildings are visible looking in from that side.'],
      ['Tall hides short', 'A building is hidden if a taller one stands in front of it.'],
      ['Blank edges', 'An edge without a number gives no clue.'],
    ],
    legend: ['Numbers outside the grid are the visibility clues.', 'The answer key prints any given numbers in bold.'],
    faq: [
      ['What do clues of 1 and N mean?', 'A 1 means the tallest building stands next to that clue. N means every building is visible, so the heights run 1, 2, 3 and so on up to N.'],
    ],
  },
  {
    slug: 'lits',
    name: 'LITS',
    generic: true,
    gameSlug: 'lits',
    playPath: '/play/lits',
    dataUrl: levels('lits'),
    renderer: 'lits',
    blurb: 'Shade one L, I, T or S shape in every region.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable LITS Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable LITS puzzles with answers. Shade one L, I, T or S tetromino in every region so the shading connects, with no 2x2 blocks. 6x6, 8x8 and 10x10 PDF worksheets.',
    keywords: 'printable lits puzzle, lits puzzle pdf, tetromino puzzle printable, shading puzzle printable, printable logic puzzles',
    h1: 'Free Printable LITS Puzzles',
    lead: 'Shade a four-cell L, I, T or S shape in every outlined region. All the shading must join up, with no 2x2 blocks.',
    about: '<strong>LITS</strong> is named after the four tetromino shapes it uses: L, I, T and S. Each outlined region gets exactly one of them. The shaded shapes must form a single connected group, never fill a 2x2 square, and two identical shapes may not touch along a region border.',
    rules: [
      ['One shape per region', 'Shade exactly four connected cells in every region, making an L, I, T or S shape.'],
      ['All connected', 'All the shaded cells across the grid form one connected group.'],
      ['No 2x2 blocks', 'No 2x2 square may be fully shaded.'],
      ['No twins touching', 'Two identical shapes may not touch along an edge, even if one is turned or flipped.'],
    ],
    legend: ['Thick lines outline the regions.', 'Shaded cells in the answer key show the shapes.'],
    faq: [
      ['Do rotated or mirrored shapes count as the same?', 'Yes. An L turned around or flipped over is still an L, so two of them may not touch.'],
      ['Where do I start a LITS?', 'Begin with the four-cell regions: they are already a shape and must be fully shaded.'],
    ],
  },
  {
    slug: 'futoshiki',
    name: 'Futoshiki',
    generic: true,
    gameSlug: 'futoshiki',
    playPath: '/play/futoshiki',
    dataUrl: levels('futoshiki'),
    renderer: 'futoshiki',
    blurb: 'A Latin square with greater-than and less-than signs between cells.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Futoshiki Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Futoshiki puzzles with answers. Fill each row and column with 1 to N and obey the greater-than and less-than signs. 4x4, 5x5 and 7x7 PDF worksheets.',
    keywords: 'printable futoshiki, futoshiki pdf, futoshiki puzzles printable, inequality puzzle printable, printable number puzzles',
    h1: 'Free Printable Futoshiki Puzzles',
    lead: 'Fill every row and column with 1 to N. The arrows between cells point at the smaller number.',
    about: '<strong>Futoshiki</strong> means "inequality" in Japanese. It is a Latin square puzzle: no boxes, just a few given numbers and greater-than or less-than signs between neighbouring cells. The signs carry most of the information.',
    rules: [
      ['Latin square', 'Each row and column holds every number from 1 to N exactly once.'],
      ['Obey the signs', 'A sign between two cells says which one is larger. The open side faces the bigger number.'],
      ['Givens', 'Some puzzles start with a few numbers printed.'],
      ['Chains', 'A run of signs like a > b > c means the first cell is at least 3.'],
    ],
    legend: ['Signs sit in the gaps between cells.', 'The answer key prints the given numbers in bold.'],
    faq: [
      ['Where do I start a Futoshiki?', 'Follow chains of signs. A cell that is bigger than two others in a row can never be 1 or 2, and the end of a long chain is often forced to 1 or N.'],
    ],
  },
  {
    slug: 'nurikabe',
    name: 'Nurikabe',
    generic: true,
    gameSlug: 'nurikabe',
    playPath: '/play/nurikabe',
    dataUrl: levels('nurikabe'),
    renderer: 'nurikabe',
    blurb: 'Shade the sea so every number sits on an island of that size.',
    sizeOf: bySize,
    sizeLabel,
    title: 'Free Printable Nurikabe Puzzles PDF with Solutions | Logic Puzzles CA',
    description: 'Free printable Nurikabe puzzles with answers. Shade the sea so each numbered island has exactly that many cells, the sea is connected and there are no 2x2 pools. 5x5, 7x7 and 9x9 PDF worksheets.',
    keywords: 'printable nurikabe, nurikabe pdf, nurikabe puzzles printable, island puzzle printable, printable logic puzzles',
    h1: 'Free Printable Nurikabe Puzzles',
    lead: 'Every number is an island of that many cells. Shade the rest as sea: one connected sea, and no 2x2 pools.',
    about: '<strong>Nurikabe</strong> is a Japanese shading puzzle about islands and sea. Each number belongs to an island with exactly that many white cells. Everything else is shaded sea, which must form one connected body of water with no 2x2 shaded squares.',
    rules: [
      ['Island sizes', 'Each number is part of a white island with exactly that many cells.'],
      ['One number per island', 'Every island holds exactly one number, and islands never touch along an edge.'],
      ['One sea', 'All shaded cells connect into a single group.'],
      ['No pools', 'No 2x2 square may be fully shaded.'],
    ],
    legend: ['Shaded cells in the answer key are the sea.'],
    faq: [
      ['Where do I start a Nurikabe?', 'Every 1 is a finished island, so shade all four of its neighbours. A cell between two numbers is sea too, because it would join two islands.'],
    ],
  },
];

export const GENERIC_PRINTABLES = PRINTABLES.filter((p) => p.generic);
export const printableBySlug = (slug) => PRINTABLES.find((p) => p.slug === slug);
